import { useQueryClient } from '@tanstack/react-query'
import { type ReactElement, useContext, useState } from 'react'
import { z } from 'zod'
import { useAgenciesList } from '~/api/queries/agency'
import { Dropdown, Form, InputCol, Submit, TextInput } from '~/components/Form'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { SENSITIVE_PLACEHOLDER } from '~/domains/configuration/constants'
import { useToggleConfigChange } from '~/domains/configuration/hooks/useToggleConfigChange'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import {
  useAdminConfig,
  useAgencyConfig,
  useConfig
} from '~/domains/configuration/queries/config'
import { settingsPageWrapper } from '~/pages/styles/settings/Settings.css'
import { AsideActionContext } from '~/utils/contextes'
import type { ExtensionConfigSideActionProps } from '../../types'
import { etestingFields } from '../fields'
import {
  agencyOption,
  agencyOptionStatus
} from './EtestingConfigSideAction.css'

const schema = z.object(
  Object.fromEntries(etestingFields.map((field) => [field.name, z.string()]))
)

export const EtestingConfigSideAction = ({
  extension,
  state
}: ExtensionConfigSideActionProps): ReactElement => {
  const { setAsideAction } = useContext(AsideActionContext)
  const queryClient = useQueryClient()
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null)

  const { data: agencies, isLoading: isLoadingAgencies } = useAgenciesList()
  const { data: configuredAgencies } = useAdminConfig('etesting.filiale_id')

  const clientLog = useConfig('etesting.client_log')
  const clientPas = useConfig('etesting.client_pas')
  const filialeId = useAgencyConfig(
    'etesting.filiale_id',
    selectedAgencyId ?? undefined
  )

  const handleToggle = useToggleConfigChange('etesting.enabled', (enabled) =>
    enabled
      ? `${extension.name} est activé.`
      : `${extension.name} est désactivé.`
  )
  const { mutateAsync: updateConfig } = useUpdateConfig()

  const storedValues: Record<string, string> = {
    client_log: clientLog.data ?? '',
    client_pas: clientPas.data ?? '',
    filiale_id: filialeId.data ?? ''
  }

  // Rebuilt on each render, which costs nothing: the Form is remounted by its key whenever the
  // selected agency changes, and never re-reads these afterwards.
  const defaultValues = Object.fromEntries(
    etestingFields.map((field) => [
      field.name,
      field.type === 'password' ? '' : (storedValues[field.name] ?? '')
    ])
  )

  const isAgencyConfigured = (agencyId: number): boolean =>
    (configuredAgencies ?? []).some(
      (entry) =>
        entry.agency === agencyId && entry.value !== null && entry.value !== ''
    )

  const close = () => setAsideAction({ action: null })

  const handleSubmit = async (
    values: Record<string, string>
  ): Promise<void> => {
    for (const field of etestingFields) {
      const value = values[field.name] ?? ''
      // A sensitive field reads back as a placeholder, never as its value: an untouched input
      // means "keep what is stored", not "erase it".
      if (field.type === 'password' && value === '') {
        continue
      }
      if (field.level === 'agency' && selectedAgencyId === null) {
        continue
      }

      await updateConfig({
        name: field.config,
        value,
        ...(field.level === 'agency'
          ? { agency: `/v1/agencies/${selectedAgencyId}` }
          : {})
      })
    }

    // An agency-scoped write lands in ['config', name, agencyId]; the tiles read the same key
    // resolved against the caller's own agency, which has to be refreshed on its own.
    for (const field of etestingFields) {
      void queryClient.invalidateQueries({ queryKey: ['config', field.config] })
    }
  }

  const isLoadingValues =
    clientLog.isLoading || clientPas.isLoading || filialeId.isLoading

  return (
    <AsideAction title={extension.name} logo={extension.logo} onClose={close}>
      <div className={settingsPageWrapper}>
        <Toggle
          label="Activer l'extension"
          defaultState={state?.isEnabled ?? false}
          onChange={handleToggle}
        />
        {isLoadingAgencies ? (
          <Loader small />
        ) : (
          <Dropdown
            label="Agence"
            placeholder="Sélectionner une agence"
            emptyMessage="Aucune agence disponible"
            options={(agencies ?? []).map((agency) => ({
              label: agency.name,
              value: String(agency.id),
              element: (
                <div className={agencyOption}>
                  <span>{agency.name}</span>
                  {isAgencyConfigured(agency.id) && (
                    <span className={agencyOptionStatus}>activée</span>
                  )}
                </div>
              )
            }))}
            onChange={(value: string) =>
              setSelectedAgencyId(value ? Number(value) : null)
            }
          />
        )}
        {selectedAgencyId !== null &&
          (isLoadingValues ? (
            <Loader small />
          ) : (
            <Form
              key={`etesting-${selectedAgencyId}`}
              schema={schema}
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
            >
              <InputCol>
                {etestingFields.map((field) => (
                  <TextInput
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type={field.type === 'password' ? 'password' : 'text'}
                    placeholder={
                      field.type === 'password' &&
                      storedValues[field.name] === SENSITIVE_PLACEHOLDER
                        ? '••••••••'
                        : undefined
                    }
                  />
                ))}
              </InputCol>
              <Submit>Enregistrer</Submit>
            </Form>
          ))}
      </div>
    </AsideAction>
  )
}
